import type { ReactNode } from 'react';
import { useTheme } from '@/hooks/useTheme';

const DOC_VERSION = '1.0';
const DOC_DATE = '17 June 2026';

function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <h2 className="font-character text-2xl sm:text-3xl text-foreground">
      {children}
      <span
        className="mt-2 block h-px w-full"
        style={{
          background:
            'linear-gradient(to right, hsl(var(--accent-gold) / 0.5), hsl(var(--accent-gold) / 0))',
        }}
      />
    </h2>
  );
}

/** A bolded label with a description below, used for the "what we support" entries. */
function Entry({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div>
      <dt className="font-semibold text-foreground">{label}</dt>
      <dd className="mt-0.5 text-foreground/70 leading-relaxed">{children}</dd>
    </div>
  );
}

/** A small inline status tag (e.g. "not enforced", "deferred"). */
function Tag({ children }: { children: ReactNode }) {
  return (
    <span className="ml-1 rounded-full border border-border bg-muted/60 px-2 py-0.5 text-xs font-medium text-muted-foreground">
      {children}
    </span>
  );
}

/** One unsupported mechanic: bold lead-in, a tag, then the explanation. */
function NoGo({ label, tag, children }: { label: string; tag: string; children: ReactNode }) {
  return (
    <li className="leading-relaxed text-foreground/70">
      <span className="font-semibold text-foreground">{label}</span>
      <Tag>{tag}</Tag>
      {': '}
      {children}
    </li>
  );
}

export default function CreatorAboutPage() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <div className="relative flex flex-col items-center px-6 py-12 min-h-[100dvh]">
      <article className="relative z-[3] w-full max-w-3xl animate-slide-up-fade space-y-12">
        {/* Header */}
        <header className="space-y-4">
          <img
            src={isDark ? '/polymyth_logo_v1_final_dark.svg' : '/polymyth_logo_v1_final_lite.svg'}
            alt="Polymyth"
            className="h-9 w-auto drop-shadow-lg"
          />
          <h1 className="font-character text-3xl sm:text-4xl text-foreground">
            Building an Adventure for Polymyth
          </h1>
          <p className="text-lg text-foreground/70 leading-relaxed">
            We are here to guide you in your journey of creating an awesome adventure for Polymyth!
            This guide is designed to get you started. Part 1 outlines the platform features, so you
            can script an adventure that will work with the game engine. Part 2 is what the engine
            does not support yet, design around it. The test for any encounter, puzzle, gate, or
            reward: it must be solvable by <em>any</em> of the supported classes using described
            actions, ability checks, and the supported spell list.
          </p>
          <p className="text-sm text-foreground/50">
            Version {DOC_VERSION} &middot; {DOC_DATE}
          </p>
        </header>

        {/* Part 1: Supported Features */}
        <section className="space-y-5">
          <SectionHeading>Supported Features</SectionHeading>
          <dl className="space-y-4">
            <Entry label="Classes (11 playable)">
              Barbarian, Bard, Cleric, Druid, Fighter, Monk, Paladin, Ranger, Sorcerer, Warlock,
              Wizard. One subclass per class (Berserker, College of Lore, Life Domain, Circle of the
              Land, Champion, Open Hand, Oath of Devotion, Hunter, Draconic, Fiend, Evoker). The
              supported play band is levels 1 to 3.
            </Entry>
            <Entry label="Species (11) and lineages">
              Dragonborn, Dwarf, Elf, Gnome, Goliath, Half-Elf, Half-Orc, Halfling, Human, Orc,
              Tiefling. Elf lineages: High Elf, Wood Elf, Drow. Ancestry features are live (Goliath
              Large Form, Dragonborn breath weapon and flight, Tiefling innate magic, and so on).
            </Entry>
            <Entry label="Backgrounds (4)">Acolyte, Criminal, Sage, Soldier.</Entry>
            <Entry
              label={
                <>
                  Feats (17, all{' '}
                  <a
                    href="https://media.dndbeyond.com/compendium-images/srd/5.2/SRD_CC_v5.2.1.pdf"
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary underline underline-offset-2 hover:text-primary/80"
                  >
                    SRD
                  </a>
                  )
                </>
              }
            >
              Alert, Magic Initiate, Savage Attacker, Skilled, Ability Score Improvement, Grappler,
              the four Fighting Style feats (Archery, Defense, Great Weapon Fighting, Two-Weapon
              Fighting), and the seven Epic Boons. No exclusions.
            </Entry>
            <Entry label="Spells (~340)">
              The SRD spell catalog across every supported class, minus the summon and
              transformation spells listed below. Spell slots, concentration, and prepared or known
              casting are modeled.
            </Entry>
            <Entry label="Magic items (~257 SRD), plus your own">
              The full SRD 5.2.1 catalog: weapons, armor, potions, rings, rods, staves, wands, and
              wondrous items. You can also invent new magic items using the engine&apos;s effect
              system (cast-spell, grant-buff, deal-damage, heal, apply-condition, charges, on-hit,
              detection, and more), and they are fully supported in a module.
            </Entry>
            <Entry label="Equipment (full SRD)">
              The full equipment system: main-hand, off-hand, and two-handed weapons, shields, and
              armor (plus head, cloak, gloves, and boots). What a character wields and wears drives
              their AC, attacks, and weapon properties.
            </Entry>
            <Entry label="Monsters (322 SRD), plus your own">
              The full SRD bestiary, with 235 in CR 0 to 5 (the level 1 to 3 sweet spot). A module
              can also provide its own monsters and NPCs with custom attacks, weapons, items,
              inventory, stats, tactics, strategy, goals, and objectives.
            </Entry>
            <Entry label="Core mechanics">
              d20 checks and saves, advantage and disadvantage, the action economy (action, bonus
              action, reaction, movement), conditions, concentration, opportunity attacks, cover,
              resistances and immunities, short and long rests (where a scene allows), milestone and
              XP leveling, and quest-flag-driven boons, gates, traps, and rewards (the backbone of
              module scripting).
            </Entry>
            <Entry label="How a round resolves">
              Play and time advance in rounds. Each round, every character&apos;s intent, the
              player, the party NPCs, and the monsters, is processed and resolved together, along
              with environmental hazards and any triggered events. Combat is settled
              deterministically (attack rolls, saving throws, damage, conditions) while the AI DM
              handles the fiction. Every action and world-state change is then narrated to the
              player and to all present characters. A combat round is a minute, an exploration round
              is ten, and a round currently takes about 5 to 15 seconds to execute.
            </Entry>
            <Entry label="Class resources">
              Every class&apos;s resource pool is tracked, rage, ki, sorcery points, channel
              divinity, bardic inspiration, superiority dice, pact magic, wild shape uses, and the
              rest, spent in play and restored on the right rest.
            </Entry>
            <Entry label="Conditions">
              The SRD conditions, blinded, charmed, frightened, grappled, poisoned, prone,
              restrained, stunned, and the rest, are applied, enforced, and cleared by the engine,
              in combat and roleplay alike.
            </Entry>
            <Entry label="Rest and recovery">
              A long rest can be taken only once every 12 hours of game time and applies
              automatically when it can; a player can call a short rest where the scene allows. Back
              in the lobby, party NPCs level up on their own when they return from an adventure.
            </Entry>
            <Entry label="Create and level up">
              Players build their character with Brother Aldric, a guided creator, then level up
              with Kira, who walks them through each step and can recommend what to choose. The full
              SRD level-up mechanics are supported, both the automatic changes (hit points,
              proficiency, feature grants) and the player&apos;s choices (feats, ability boosts,
              spells), including the one subclass per class, chosen at level 3.
            </Entry>
            <Entry label="Alignment and roleplay XP">
              Full SRD alignment is supported, and the DM rewards it: a player earns experience for
              roleplaying true to their alignment, most of all when it creates real tension or calls
              for a sacrifice.
            </Entry>
            <Entry label="Tavian, your guide">
              The Portal Master who greets players in the lobby, briefs each adventure (a module can
              write its own briefing), helps them pick a module and party, and is always on hand for
              advice. Aldric, Kira, Tavian, and the Inner Voice are all reasoning characters that
              know the applicable rules, not scripted menus.
            </Entry>
            <Entry label="The Inner Voice">
              Every player has an always-open private channel to their own character, an intimate
              inner dialogue, separate from the DM and from party chat. The character is its own
              being, with ideals and ambitions, and it works with the player on how to grow, fight,
              and roleplay, even recommending concrete ways to get better: equipment, items, and
              build choices.
            </Entry>
            <Entry label="Death and dying">
              A character at 0 HP drops and rolls death saves each round; if the whole party falls
              it is a TPK and the run ends. A revived hero carries the resurrection penalty (see the
              house rules below).
            </Entry>
            <Entry label="Session recaps">
              When a session ends the DM writes a narrative recap from the journal, and returning
              players open on a &ldquo;Previously&rdquo; catch-up, so a long campaign always picks
              up where it left off.
            </Entry>
            <Entry label="Persistent characters and campaigns">
              A player&apos;s hero persists: the same character carries from one adventure to the
              next, leveling and gathering gear across a campaign that can span many sessions. The
              journal and recaps keep the through-line.
            </Entry>
            <Entry label="Custom mechanics (the scripts system)">
              Beyond the SRD, a module defines its own logic declaratively. Quest flags gate scenes,
              spawn NPCs, reveal objects, and fire effects (damage reduction, combat bonuses, XP);
              objectives grant XP for creator-defined milestones (your own achievements and
              progress); triggers fire on item pickups, scene arrival, or a companion joining; and
              flag-gated exits and resolutions drive the story. This is how custom traps, riddles,
              multi-step quests, and bespoke progression are built, as in Goldvale&apos;s Elara
              rescue.
            </Entry>
            <Entry label="Puzzles and quests">
              Puzzles accept riddle answers or skill checks (a DC and a skill), offer optional
              hints, and carry their own success and failure effects, so a wrong answer can spring a
              trap. Quests track many ordered objectives in a journal and pay out gold and XP on
              completion, your own multi-step storylines with real rewards.
            </Entry>
            <Entry label="The journal">
              A full adventure log that writes itself, with smart summaries of the adventure&apos;s
              important moments. Every quest, objective, event, and milestone is captured as it
              happens, the player&apos;s record of the story so far and the spine the session recaps
              are built from.
            </Entry>
            <Entry label="NPC and monster minds">
              Every NPC and monster can be written with goals, flaws, a personality, a long-term
              strategy, and tactics, both how it fights and how it behaves in roleplay. Each also
              carries knowledge and secrets it shares or withholds based on quest flags, so a
              character reveals only what the party has earned the right to learn. NPCs act in
              character, by their personality, their current condition, and the state of the world.
              Scene NPCs can also be invited to join the party: a creator decides who is open to it
              and who always refuses, and the answer is given in character.
            </Entry>
            <Entry label="Living world time">
              World time is fully tracked per party, and every adventure opens on the morning of day
              one. It advances on its own as you play, a combat round is one minute and an
              exploration round ten. The engine knows the hour and the period of day, morning, day,
              evening, night, and uses them thematically, and a module can fire scene events or set
              quest flags based on the time elapsed since day one. The world and its time can keep
              moving rather than freezing until the player acts.
            </Entry>
            <Entry label="Locations and scenes">
              The world is built from locations, major areas like a village, a dungeon, or a forest,
              each with its own lighting, terrain, weather, danger level, atmosphere, and history,
              and a read-aloud on arrival. A location is made of scenes, the rooms the party stands
              in. Each scene has its own description and sensory detail (sights, sounds, smells),
              its own art and looping video (and a separate encounter image and video), its NPCs,
              monsters, loose items, traps, and hazards, mood overlays that rewrite it once a flag
              flips, scripted beats, and its own rest rules. Scenes connect through exits, one-way
              or two-way, sometimes through a door or object, each with a travel time, and any of
              them can be blocked or opened based on a quest flag, the party&apos;s progress, or the
              world time.
            </Entry>
            <Entry label="Interactive objects">
              Scenes are furnished with interactive objects, containers that hold loot, doors that
              lock and gate the way through, plus furniture, fireplaces, and the rest. Objects can
              be examined, opened, broken, or used for cover in a fight.
            </Entry>
            <Entry label="Explore and travel">
              The party explores by moving between scenes and locations along paths, from an
              adjacent room to a distant location. Every hop runs the same way and costs game time,
              advancing the world clock.
            </Entry>
            <Entry label="Maps">
              A module can ship a world or campaign map; hand the engine a rough sketch and it
              becomes a polished, professional map. Show it from the start, or have the party
              acquire it in play (found, bought, or earned), so the map appears only once they hold
              it.
            </Entry>
            <Entry label="Reactive scenes">
              Scenes change with the story: NPCs spawn or vanish on quest flags, mood overlays
              rewrite a room&apos;s description and senses once a flag flips, and objects and exits
              stay hidden or locked until the party earns them.
            </Entry>
            <Entry label="Dynamic encounters">
              An encounter is more than a stat line: lair actions, per-round and periodic events,
              scripted beats, and machine-readable boss mechanics let a fight escalate and surprise
              on a timer or a trigger.
            </Entry>
            <Entry label="Monster morale (flee or fight)">
              An encounter sets how its monsters break when the tide turns: fight to the death, or
              flee and retreat into an adjacent scene.
            </Entry>
            <Entry label="Initiating Combat">
              Scenes start in roleplay: monsters and hostile NPCs warn, threaten, taunt, or
              negotiate before any blade is drawn, and combat begins only when the player commits a
              hostile action. A specific encounter can also be scripted to open hostile, ambushing
              the party on entry or on a condition you set.
            </Entry>
            <Entry label="Hazards and traps">
              Lava pools, spike floors, toxic gas, falling icicles, thin ice, and other hazards and
              traps, sprung by stepping somewhere, a loud noise, an opened door, or a wrong puzzle
              answer. A trap can be spotted (a Perception check) and disarmed (a check, though some
              cannot be), and when it springs it forces a saving throw against its damage. One-shot
              or continuous, in combat and exploration alike.
            </Entry>
            <Entry label="Media, generated for you">
              You bring sketches, a written description, or a style; Polymyth creates the whole
              module&apos;s media: every video, image, and NPC or monster portrait, plus the
              soundtrack and sound effects. You supply none of the finished assets, and you can
              steer any piece with your own artistic direction and reference illustrations.
            </Entry>
            <Entry label="Atmosphere (music and art)">
              Every scene is a living, animated backdrop, a looping video rather than a static
              image, with its own soundtrack cues (combat, exploration, victory) and generated
              illustrations: scene art, NPC and monster portraits, and item cuts, all in the locked
              black-and-white engraving style.
            </Entry>
            <Entry label="Cinematic videos">
              Generated short videos play at the moments that matter: a clip per encounter, per
              scene, and on key event beats (a dragon flyby), an animated world map, and an
              adventure-completion celebration when the party wins.
            </Entry>
            <Entry label="Items">
              Items are picked up from scenes as loot, and currently only the player character can
              pick them up; it is on them to share, though items can be handed freely between party
              members. Potions, buffs, and healing spells all work in both roleplay and combat
              encounters.
            </Entry>
            <Entry label="The lobby store">
              Between adventures, players gear up at the lobby store, run by Grolt the merchant: a
              full catalog to buy from, and they can sell any item from their inventory back at
              Grolt&apos;s estimated value. A module can also stock its own module-specific items
              that players are expected to buy.
            </Entry>
            <Entry label="Practice duels">
              In the lobby, a player can challenge any NPC to a one-on-one duel, a low-stakes way to
              practice combat and test their kit against a live opponent.
            </Entry>
            <Entry label="Party">
              Currently solo play: one human player character plus NPC companions. (Multiple human
              PCs in one party are coming soon, not yet supported.) NPCs join the party either in
              the lobby (before you start) or in-game (recruited during the adventure), a module
              Companion or others the party meets. The lobby keeps a roster of NPCs at a range of
              levels, but higher-level NPCs are reluctant to join a lower-level hero, so parties
              tend to sit near the player&apos;s own level. Each module sets its own party size; the
              default band is 2 to 7, balanced for 3 to 5. Design encounters for that gathered
              headcount, not for a lone hero.
            </Entry>
          </dl>
        </section>

        {/* Part 2: Unsupported Features */}
        <section className="space-y-5">
          <SectionHeading>Unsupported Features</SectionHeading>
          <p className="text-foreground/70 leading-relaxed">
            Polymyth supports about 90% of the SRD&apos;s mechanics and rules. The rest falls into
            three buckets: some features are not yet implemented, some are limited by the SRD&apos;s
            own scope, and some we decided not to support because they are rare or over-complicated.
            Whatever the reason, do not build an adventure that <em>requires</em> any of these.
          </p>

          <div className="space-y-2">
            <h3 className="font-semibold text-foreground">Classes</h3>
            <ul className="list-disc space-y-2 pl-5">
              <NoGo label="Rogue" tag="not playable">
                the one SRD class we do not offer. Never gate anything on rogue skills or
                thieves&apos; tools. Traps, locks, and stealth must be solvable by any party (spot
                with Perception or Investigation, then disarm or break with a described action or a
                Strength or Dexterity check).
              </NoGo>
              <NoGo label="Multiclassing" tag="not supported">
                every PC is a single class for the whole adventure.
              </NoGo>
              <NoGo label="Extra subclasses" tag="not supported">
                one canonical subclass per class only; do not assume any other.
              </NoGo>
            </ul>
          </div>

          <div className="space-y-2">
            <h3 className="font-semibold text-foreground">Spells</h3>
            <ul className="list-disc space-y-2 pl-5">
              <NoGo label="Summons and conjures" tag="removed">
                no persistent controlled creatures (Find Familiar, Find Steed, Unseen Servant,
                Conjure Animals, Animate Dead, Phantom Steed, Conjure Minor Elementals, Conjure
                Elemental). Spirit Guardians is the exception (an aura, not a creature). No
                encounter may assume a summoned ally, familiar, steed, or raised undead.
              </NoGo>
              <NoGo label="Transformations" tag="not supported">
                Polymorph, True Polymorph, Shapechange, Animal Shapes, and the form-change clause of
                Alter Self and Gaseous Form; Druid Wild Shape is narrated, not simulated. Never
                require the party to shapeshift to progress.
              </NoGo>
              <NoGo label="Heat Metal, Phantasmal Force loop" tag="removed">
                Heat Metal is out; Phantasmal Force is a one-shot illusion with no per-turn damage
                loop.
              </NoGo>
            </ul>
          </div>

          <div className="space-y-2">
            <h3 className="font-semibold text-foreground">Class features</h3>
            <ul className="list-disc space-y-2 pl-5">
              <NoGo label="Druid Wild Shape" tag="roleplay only">
                no beast form on the map (the resource pool still powers Circle of the Land
                features).
              </NoGo>
              <NoGo label="Warlock Pact of the Chain" tag="disabled">
                its familiar is a persistent summon. Pact of the Blade and Pact of the Tome are
                fully supported.
              </NoGo>
              <NoGo label="High-level feature choices" tag="out of scope">
                anything past the level 1 to 3 band (Wild Magic, Brutal Strike, Cunning Strike) is
                not wired. Design for levels 1 to 3.
              </NoGo>
            </ul>
          </div>

          <div className="space-y-2">
            <h3 className="font-semibold text-foreground">Game mechanics</h3>
            <ul className="list-disc space-y-2 pl-5">
              <NoGo label="Spell components (verbal, somatic, material)" tag="not enforced">
                no &ldquo;silenced means no casting,&rdquo; no grapple-breaks-casting, no
                material-cost deduction. Don&apos;t gate on stripping a caster&apos;s components.
              </NoGo>
              <NoGo label="Ritual casting" tag="not implemented">
                ritual-tagged spells cast as their normal action, with no 10-minute ritual.
              </NoGo>
              <NoGo label="Spell preparation swaps" tag="locked per session">
                a caster&apos;s prepared list is fixed once the session begins.
              </NoGo>
              <NoGo label="Encumbrance and ammunition" tag="not tracked">
                weight is ignored and arrows, bolts, and bullets never run out.
              </NoGo>
              <NoGo label="Identify, per-enemy stealth" tag="simplified">
                items reveal their properties on inspect; stealth is one DC versus the whole
                encounter, not each enemy&apos;s passive Perception.
              </NoGo>
              <NoGo label="Climbing, mounts, spell-created terrain" tag="not modeled">
                no climb-speed or vertical grid, no mount or passenger model, and Spike Growth or
                Plant Growth do not slow anyone. Don&apos;t require scaling a wall, riding a mount,
                or walling a path with terrain spells.
              </NoGo>
              <NoGo label="Random encounters" tag="not yet">
                traveling between scenes or resting does not roll wandering monsters or events;
                encounters are placed by the author, not generated on the road or in camp.
              </NoGo>
            </ul>
          </div>

          <p className="text-foreground/70 leading-relaxed">
            <span className="font-semibold text-foreground">Feats:</span> no exclusions: all 17 SRD
            feats are supported. Non-SRD feats are not allowed at all (a content-legality rule,
            separate from this list).
          </p>
        </section>

        {/* Part 3: House rules */}
        <section className="space-y-5">
          <SectionHeading>House rules</SectionHeading>
          <p className="text-foreground/70 leading-relaxed">
            Intentional choices that differ from the 2024 SRD. Plan around them.
          </p>
          <ul className="list-disc space-y-2 pl-5 text-foreground/70">
            <li className="leading-relaxed">
              <span className="font-semibold text-foreground">Round length.</span> A round is not
              the SRD&apos;s six seconds, in combat it is one minute, and out of combat it is ten,
              so the world clock moves at a brisker, story-friendly pace.
            </li>
            <li className="leading-relaxed">
              <span className="font-semibold text-foreground">Resurrection penalty.</span> A
              character raised from death takes -4 to all d20 rolls (attacks, saves, checks), fading
              by 1 per long rest. The SRD has no such penalty, so a revived hero is weakened for a
              while.
            </li>
            <li className="leading-relaxed">
              <span className="font-semibold text-foreground">No separate social mode.</span> The
              engine runs Combat or Exploration only; NPC conversations happen inside Exploration.
              Write social beats as exploration scenes.
            </li>
            <li className="leading-relaxed">
              <span className="font-semibold text-foreground">Warlock specifics.</span> One Pact
              Boon, locked once chosen; Pact of the Blade always uses Charisma; Pact of the Chain is
              disabled. Blade, Tome, Armor of Shadows, and Eldritch Mind are fully supported.
            </li>
            <li className="leading-relaxed">
              <span className="font-semibold text-foreground">Leave any time.</span> A player can
              end the session and return to the lobby at any time (outside of combat); the world
              state is saved, so the next session resumes where they left off.
            </li>
          </ul>
        </section>

        {/* The one rule of thumb */}
        <section className="space-y-3">
          <SectionHeading>The one rule of thumb</SectionHeading>
          <p className="text-foreground/80 leading-relaxed">
            If an encounter, puzzle, gate, or reward only works because a PC can transform, summon a
            creature, climb or fly, pick a lock with thieves&apos; tools, or run out of ammo,
            redesign it so any of the 11 classes can solve it with described actions, ability
            checks, and the supported spell list. That is the whole test.
          </p>
        </section>
      </article>
    </div>
  );
}
